import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import {
	AuthService,
	NotifyService,
	SKIP_FORBIDDEN_NOTICE,
	SubscriptionStateService,
} from '@app/core/services';
import { environment } from '@app/env';
import { WA_WINDOW } from '@ng-web-apis/common';
import { catchError, throwError } from 'rxjs';

/**
 * Motivos de un 403 que sabemos explicar, por el `code` que manda el backend
 * (`ForbiddenCode` en `chatgpt-nodejs`). Cualquier otro cae en el genérico:
 * lista cerrada CON salida, nunca la opción menos mala.
 */
const FORBIDDEN_REASONS: Record<string, string> = {
	subscription_invalid: 'notification.error.subscription_invalid',
	not_rendering_provider: 'notification.error.not_rendering_provider',
};

/**
 * Motivos de un 401 (`UnauthorizedCode` en `chatgpt-nodejs`). En los dos el
 * login es el remedio, así que los dos navegan; lo que cambia es qué se le
 * dice al profesional antes de mandarlo allí. "Has entrado desde otro sitio"
 * y "se te acabó la sesión" no son lo mismo para quien lo lee.
 */
const UNAUTHORIZED_REASONS: Record<string, string> = {
	session_invalid: 'notification.error.session_invalid',
	session_superseded: 'notification.error.session_superseded',
};

function reasonFor(
	error: HttpErrorResponse,
	reasons: Record<string, string>,
	fallback: string
): string {
	const code = (error.error as { code?: string } | null)?.code;
	return (code && reasons[code]) || fallback;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
	const authService = inject(AuthService);
	const window = inject(WA_WINDOW);
	const notify = inject(NotifyService);
	// Se inyecta aquí y no dentro del `catchError`: allí ya no hay contexto de
	// inyección y `inject()` lanzaría.
	const subscriptionState = inject(SubscriptionStateService);

	if (authService.isAuth) {
		req = req.clone({
			setHeaders: { Authorization: `Bearer ${authService.JWT}` },
		});
	}

	return next(req).pipe(
		catchError((error: HttpErrorResponse) => {
			// 401 — no estás autenticado. El login SÍ es el remedio.
			//
			// Hasta ahora el backend casi nunca llegaba a decir 401: un token
			// caducado salía como 403 «Forbidden resource» (y desde H23 el 403
			// ya no navega, así que el usuario se quedaba sin salida), y un
			// token desplazado por un segundo login salía como 500. Con los dos
			// casos diciendo 401 y con su código, esta rama por fin se recorre
			// cuando toca y puede explicar cuál de los dos fue.
			if (error.status === 401) {
				notify.notify(
					reasonFor(error, UNAUTHORIZED_REASONS, 'notification.error.auth'),
					'warning'
				);

				const currentUrl = window.location.href;
				window.location.href = `${environment.MAIN_DOMAIN}/auth/login?returnUrl=${encodeURIComponent(currentUrl)}&callback=external`;
			}

			// 403 — estás autenticado y aun así no puedes. El login NO es el
			// remedio: vuelves a entrar como el mismo usuario y chocas con el
			// mismo muro.
			//
			// Antes los dos casos caían en la misma rama y un 403 te expulsaba
			// a la aplicación principal. Medido el 2026-07-26: al firmar una
			// nota de BCaBA sin ese rol, el backend respondía 403 explicando
			// por qué, y el usuario acababa en el dashboard de otra aplicación
			// sin llegar a leer el motivo NI que se le pidiera la contraseña
			// —su sesión era válida—, con la nota sin firmar y la pantalla
			// perdida. Con la suscripción vencida pasa lo mismo, y ese caso va
			// a ser el habitual en producción.
			//
			// Así que aquí no se navega a ningún sitio: se dice qué pasa y el
			// profesional se queda donde estaba, con su trabajo delante.
			//
			// Con la suscripción vencida, además, la pantalla pasa a solo lectura
			// en el acto: venció con el chat abierto y no tiene sentido dejar el
			// campo de mensaje activo hasta la siguiente navegación.
			if (error.status === 403) {
				const code = (error.error as { code?: string } | null)?.code;
				if (code === 'subscription_invalid') subscriptionState.markExpired();

				// `check-subscription` responde 403 cuando está vencida: ahí es la
				// respuesta que se pregunta, no un error que avisar.
				if (!req.context.get(SKIP_FORBIDDEN_NOTICE))
					notify.notify(
						reasonFor(error, FORBIDDEN_REASONS, 'notification.error.forbidden'),
						'warning'
					);
			}

			// 503 — no se pudo comprobar la sesión porque la API principal no
			// respondió. La sesión puede estar perfectamente bien, así que aquí
			// no se navega a ningún sitio: mandar al login por una caída de
			// infraestructura le haría perder el trabajo que tenga delante para
			// acabar en una pantalla que tampoco va a funcionar.
			if (error.status === 503) {
				notify.notify('notification.error.server', 'warning');
			}

			return throwError(() => error);
		})
	);
};
